import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useParams } from 'react-router-dom';
import { studentProfilePath } from './advisors';
import CommonHeader from '../../../util_components/common_header';
import UserCombobox from '../../../util_components/user_combobox/UserCombobox';
import CustomModal from '../../../util_components/custom_modal';
import * as actions from '../../../../redux/action_creators';
import { hasAccess } from '../../../../util_functions';
import AdvisorHistoryModal from './AdvisorHistoryModal';
import { useAdvisorAccess } from './useCanManageAdvisors';
import {
  SOURCE_NOT_ACTIVE,
  TARGET_INELIGIBLE,
  apiErrorCode,
  apiErrorReason,
  apiStatus,
  commitReassign,
  endAssignment,
  fetchAdvisors,
  previewReassign
} from './api';
import {
  ADVISORS_TAB_PATH,
  EMPTY_FILTERS,
  MAX_COMMIT_ITEMS,
  PREVIEW_CAP,
  REASSIGN_ACTION,
  SOURCES,
  advisorOptions,
  advisorPagePath,
  commitRequests,
  daysLeft,
  eligibleTargets,
  endNotice,
  endRequest,
  formatDay,
  formatTs,
  hasNarrowingFilters,
  listQuery,
  outcomeLabel,
  previewByAdvisor,
  previewByStudents,
  previewTruncated,
  runCommit,
  skipReasonLabel,
  sourceLabel,
  studentNames
} from './advisors';
import type { Filters } from './advisors';
import { SHARED_STYLES } from '../styles';
import { PILL, PLAIN_PILL, WARN_PILL } from './pills';
import StackTd from '../StackTable';
import { stackClasses } from '../stack_table';
import type { Notify } from './AdvisorsPanel';
import type { AdvisorRow, AdvisorSummary, Commit, Preview, PreviewItem } from './types';

interface PreviewState {
  preview: Preview;
  to: string;
  byAdvisor: boolean;
}

const { MUTED, PRIMARY_BTN, SECONDARY_BTN, FIELD } = SHARED_STYLES;

// Fixed widths (646px) leave Student ~137px at a 1024px viewport with the sidebar open.
const ROW_COLS: { label: string; width?: number }[] = [
  { label: '', width: 36 },
  { label: 'Student' },
  { label: 'Source', width: 140 },
  { label: 'Assigned', width: 104 },
  { label: 'Expires', width: 110 },
  { label: 'Assigned by', width: 120 },
  { label: '', width: 136 }
];
const CELL = 'px-3 py-2.5 text-left';
const ST = stackClasses();
const SMALL_BTN = 'rounded-md px-2 py-1 text-xs font-medium';
const BTN =
  'rounded-md px-4 py-1.5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50';
const RESULT_COLLAPSED = 5;

const dash = (v: string | null | undefined) => v || '—';
const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;

interface Props {
  advisorUuid: string;
  notify: Notify;
  // can_write_advisors (/me); without it the page is view-only: filters and History, no writes.
  canWrite: boolean;
}

// One advisor's active students: filter, reassign, end, history.
export function AdvisorStudents({ advisorUuid, notify, canWrite }: Props) {
  const router = useHistory();
  const base: Filters = { ...EMPTY_FILTERS, advisor: advisorUuid };
  const [draft, setDraft] = useState<Filters>(base);
  const [applied, setApplied] = useState<Filters>(base);
  const [summary, setSummary] = useState<AdvisorSummary[]>([]);
  const [rows, setRows] = useState<AdvisorRow[]>([]);
  const [nextCursor, setNextCursor] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState<{ forbidden: boolean; reason: string } | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState(false);
  const [previewState, setPreviewState] = useState<PreviewState | null>(null);
  const [result, setResult] = useState<Commit | null>(null);
  const [resultExpanded, setResultExpanded] = useState(false);
  // Captured at commit time: the reload drops moved students from the filtered rows.
  const [resultItems, setResultItems] = useState<PreviewItem[]>([]);
  const [historyStudent, setHistoryStudent] = useState<{ uuid: string; name: string } | null>(null);
  const [endTarget, setEndTarget] = useState<AdvisorRow | null>(null);
  const [ending, setEnding] = useState(false);
  // Drops responses from a superseded load (filter changed mid-flight).
  const loadSeq = useRef(0);

  const load = useCallback(
    (filters: Filters) => {
      loadSeq.current += 1;
      const seq = loadSeq.current;
      setLoading(true);
      return fetchAdvisors(listQuery(filters))
        .then((d) => {
          if (seq !== loadSeq.current) return;
          setSummary(d.summary);
          setRows(d.rows);
          setNextCursor(d.next_cursor);
          setLoadError(null);
        })
        .catch((e: unknown) => {
          if (seq !== loadSeq.current) return;
          const forbidden = apiStatus(e) === 403;
          const reason = forbidden
            ? 'Super user or sales manager only'
            : apiErrorReason(e, 'Failed to load advisors');
          // Never leave the previous filter's rows (and a live "Reassign all") under new inputs.
          setSummary([]);
          setRows([]);
          setNextCursor(0);
          // The target box goes blank with the summary, so drop the hidden target and open preview too.
          setTarget('');
          setPreviewState(null);
          setLoadError({ forbidden, reason });
          if (!forbidden) notify('error', reason);
        })
        .finally(() => {
          if (seq === loadSeq.current) setLoading(false);
        });
    },
    [notify]
  );

  useEffect(() => {
    load(applied);
  }, [load, applied]);

  const apply = (next: Filters) => {
    setDraft(next);
    setApplied(next);
    setSelected(new Set());
    setPreviewState(null);
    setResult(null);
  };

  const resetFilters = () => apply({ ...EMPTY_FILTERS, advisor: advisorUuid });

  const reload = () => {
    setSelected(new Set());
    return load(applied);
  };

  const loadMore = () => {
    if (!nextCursor || loadingMore) return;
    const seq = loadSeq.current;
    setLoadingMore(true);
    fetchAdvisors(listQuery(applied, nextCursor))
      .then((d) => {
        if (seq !== loadSeq.current) return;
        setRows((prev) => [...prev, ...d.rows]);
        setNextCursor(d.next_cursor);
      })
      .catch((e: unknown) => notify('error', apiErrorReason(e, 'Failed to load more students')))
      .finally(() => setLoadingMore(false));
  };

  const toggle = (uuid: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });

  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.student_uuid));
  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.student_uuid)));

  const advisor = summary.find((s) => s.cms_user_uuid === advisorUuid);
  const targets = useMemo(
    () => eligibleTargets(summary).filter((t) => t.uuid !== advisorUuid),
    [summary, advisorUuid]
  );
  const advisors = useMemo(() => advisorOptions(summary), [summary]);
  const unknownAdvisor = !loading && !loadError && !advisor;
  const advisorName = advisor?.name || '';

  const switchAdvisor = (uuid: string) => {
    if (uuid && uuid !== advisorUuid) router.push(advisorPagePath(uuid));
  };

  const startPreview = (body: ReturnType<typeof previewByStudents>) => {
    if (busy) return;
    setBusy(true);
    setResult(null);
    previewReassign(body)
      .then((preview) =>
        setPreviewState({ preview, to: target, byAdvisor: 'from_cms_user_uuid' in body })
      )
      .catch((e: unknown) => {
        const reason = apiErrorReason(e, 'Failed to preview reassignment');
        notify(
          'error',
          apiErrorCode(e) === TARGET_INELIGIBLE
            ? `Target advisor is not eligible: ${reason}`
            : reason
        );
      })
      .finally(() => setBusy(false));
  };

  const onPreviewSelected = () => startPreview(previewByStudents(Array.from(selected), target));
  const onPreviewAll = () => startPreview(previewByAdvisor(advisorUuid, target));

  const onConfirm = () => {
    if (!previewState || busy) return;
    const { preview, to } = previewState;
    const requests = commitRequests(preview, to);
    if (!requests.length) return;
    setBusy(true);
    runCommit(preview.batch_ref, requests, commitReassign)
      .then(({ commit, error }) => {
        setResultItems(preview.items);
        setResult(commit);
        setResultExpanded(false);
        setPreviewState(null);
        // Any open history may now be stale.
        setHistoryStudent(null);
        if (error) {
          notify(
            'error',
            `${apiErrorReason(error, 'Commit failed')}. ${commit.counts.reassigned} reassigned before the error; retry is safe.`
          );
        } else {
          notify('success', `Reassigned ${commit.counts.reassigned} student(s)`);
        }
        return reload();
      })
      .finally(() => setBusy(false));
  };

  // Reload on success and on SOURCE_NOT_ACTIVE; a generic error keeps the modal and list.
  const onEndConfirm = () => {
    if (!endTarget || ending) return;
    const row = endTarget;
    const closeHistory = () =>
      setHistoryStudent((h) => (h && h.uuid === row.student_uuid ? null : h));
    setEnding(true);
    endAssignment(endRequest(row))
      .then((res) => {
        notify('success', endNotice(row, res));
        setEndTarget(null);
        closeHistory();
        return reload();
      })
      .catch((e: unknown) => {
        if (apiErrorCode(e) === SOURCE_NOT_ACTIVE) {
          notify('error', 'This assignment already changed');
          setEndTarget(null);
          closeHistory();
          return reload();
        }
        notify('error', apiErrorReason(e, 'Failed to end the assignment'));
        return null;
      })
      .finally(() => setEnding(false));
  };

  const headerName = () => {
    if (advisor) return advisor.name;
    if (loading) return 'Loading…';
    return unknownAdvisor ? 'Unknown advisor' : 'Advisor';
  };

  const renderHeader = () => (
    <div className="px-5 py-4 border-b border-line flex flex-wrap items-center justify-between gap-3">
      <div className="min-w-0">
        <Link to={ADVISORS_TAB_PATH} className="text-xs text-content-muted hover:underline">
          ← All advisors
        </Link>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <h2 className="text-lg font-semibold">{headerName()}</h2>
          {advisor ? (
            <>
              {advisor.level ? <span className={PLAIN_PILL}>{advisor.level}</span> : null}
              <span className={advisor.roster_status === 'LEFT' ? WARN_PILL : PLAIN_PILL}>
                {advisor.roster_status === 'LEFT' ? 'Left the roster' : 'Active roster'}
              </span>
              <span className={advisor.has_sales ? PLAIN_PILL : WARN_PILL}>
                {advisor.has_sales ? 'SALES role' : 'No SALES role'}
              </span>
              <span className="text-xs text-content-muted">
                {plural(advisor.active_students, 'active student')}
              </span>
            </>
          ) : null}
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <UserCombobox
          id="advisor-switch"
          label="Switch advisor"
          placeholder="Switch advisor…"
          users={advisors}
          value={advisorUuid}
          on_change={switchAdvisor}
          width={220}
        />
        <span className="text-xs text-content-muted">Times in IST</span>
      </div>
    </div>
  );

  const renderFilters = () => (
    <form
      className="px-5 py-3 flex flex-row flex-wrap items-end justify-start gap-3 border-b border-line"
      onSubmit={(e) => {
        e.preventDefault();
        apply(draft);
      }}
    >
      <label htmlFor="advisors-filter-source" className="text-xs" style={MUTED}>
        Source
        <select
          id="advisors-filter-source"
          value={draft.source}
          onChange={(e) => apply({ ...draft, source: e.target.value as Filters['source'] })}
          className="block text-sm"
          style={FIELD}
        >
          <option value="">Any</option>
          {SOURCES.map((s) => (
            <option key={s} value={s}>
              {sourceLabel(s)}
            </option>
          ))}
        </select>
      </label>
      <label htmlFor="advisors-filter-days" className="text-xs" style={MUTED}>
        Expiring within (days)
        <input
          id="advisors-filter-days"
          type="number"
          min={1}
          value={draft.expiringWithinDays}
          onChange={(e) => setDraft({ ...draft, expiringWithinDays: e.target.value })}
          className="block text-sm"
          style={{ ...FIELD, width: 96 }}
        />
      </label>
      <label htmlFor="advisors-filter-q" className="text-xs" style={MUTED}>
        Student
        <input
          id="advisors-filter-q"
          type="text"
          placeholder="Name or email"
          value={draft.q}
          onChange={(e) => setDraft({ ...draft, q: e.target.value })}
          className="block text-sm"
          style={{ ...FIELD, width: 220 }}
        />
      </label>
      <button type="submit" className={BTN} style={PRIMARY_BTN}>
        Search
      </button>
      <button type="button" onClick={resetFilters} className={BTN} style={SECONDARY_BTN}>
        Reset
      </button>
    </form>
  );

  // Read-only callers lose the select column; its header cell is the select-all box.
  const cols = canWrite ? ROW_COLS : ROW_COLS.slice(1);

  const renderActions = () =>
    canWrite ? (
      <div className="sticky top-0 z-10 bg-surface-card px-5 py-2.5 border-b border-line flex flex-wrap items-center gap-3">
        <span className="text-sm tabular-nums">{`${selected.size} selected`}</span>
        <UserCombobox
          id="advisors-target"
          label="Reassign to"
          placeholder="Reassign to…"
          users={targets}
          value={target}
          on_change={setTarget}
          disabled={busy}
          width={240}
        />
        <button
          type="button"
          onClick={onPreviewSelected}
          disabled={!target || selected.size === 0 || selected.size > MAX_COMMIT_ITEMS || busy}
          className={BTN}
          style={PRIMARY_BTN}
        >
          Preview reassign
        </button>
        <button
          type="button"
          onClick={onPreviewAll}
          disabled={!target || busy || !advisor || advisor.active_students === 0}
          className={BTN}
          style={SECONDARY_BTN}
        >
          {advisor ? `Reassign all (${advisor.active_students})` : 'Reassign all'}
        </button>
        {selected.size > MAX_COMMIT_ITEMS ? (
          <span className="text-xs" style={MUTED}>
            {`Select at most ${MAX_COMMIT_ITEMS} students, or reassign all of them.`}
          </span>
        ) : null}
      </div>
    ) : (
      <div className="px-5 py-2.5 border-b border-line text-xs" style={MUTED}>
        View only: only sales managers can assign or reassign.
      </div>
    );

  const renderPreviewModal = () => {
    const batches = previewState ? commitRequests(previewState.preview, previewState.to).length : 0;
    return (
      <CustomModal
        show={!!previewState}
        // Escape and the overlay are ignored while a commit is running.
        close={() => (busy ? undefined : setPreviewState(null))}
        title="Preview reassign"
        inner_style={{ width: 'min(860px, 96vw)', borderRadius: 12 }}
      >
        {previewState ? (
          <div role="region" aria-label="Reassignment preview">
            <h3 className="font-semibold">
              {`Reassign to ${previewState.preview.to.name}${previewState.preview.to.level ? ` (${previewState.preview.to.level})` : ''}`}
            </h3>
            <p className="mt-1 text-sm" style={MUTED}>
              {`${previewState.preview.counts.reassign} to reassign, ${previewState.preview.counts.skip} skipped.`}
              {batches > 1 ? ` Sent in ${batches} batches.` : ''}
            </p>
            {previewTruncated(previewState.preview, previewState.byAdvisor) ? (
              <p className="mt-1 text-sm" style={MUTED}>
                {`Only the first ${PREVIEW_CAP} are shown; preview again after confirming to move the rest.`}
              </p>
            ) : null}
            <div className="mt-2 overflow-auto" style={{ maxHeight: '50vh' }}>
              <table className={ST.table} aria-label="Preview items">
                <thead className={`bg-surface-sunken text-content-secondary ${ST.head}`}>
                  <tr>
                    {['Student', 'From', 'Expires', 'Action'].map((c) => (
                      <th key={c} className="px-3 py-2 text-left font-medium">
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className={ST.body}>
                  {previewState.preview.items.map((i) => (
                    <tr
                      key={`${i.student_uuid}-${i.row_id}`}
                      className={`border-t border-line ${ST.row}`}
                    >
                      <StackTd className="px-3 py-2 text-left truncate" title={i.student_name}>
                        {i.student_name}
                      </StackTd>
                      <StackTd label="From" className="px-3 py-2 text-left truncate">
                        {dash(i.from_name)}
                      </StackTd>
                      <StackTd label="Expires" className="px-3 py-2 text-left">
                        {formatTs(i.expires_at)}
                      </StackTd>
                      <StackTd label="Action" className="px-3 py-2 text-left">
                        {i.action === REASSIGN_ACTION
                          ? 'Reassign'
                          : `Skip: ${skipReasonLabel(i.skip_reason) || i.action}`}
                      </StackTd>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPreviewState(null)}
                disabled={busy}
                className={BTN}
                style={SECONDARY_BTN}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onConfirm}
                disabled={busy || previewState.preview.counts.reassign === 0}
                className={BTN}
                style={PRIMARY_BTN}
              >
                {busy
                  ? 'Reassigning…'
                  : `Confirm reassign (${previewState.preview.counts.reassign})`}
              </button>
            </div>
          </div>
        ) : null}
      </CustomModal>
    );
  };

  const renderResult = () => {
    if (!result) return null;
    const names = studentNames(resultItems, rows);
    const shown = resultExpanded ? result.results : result.results.slice(0, RESULT_COLLAPSED);
    return (
      <div
        className="bg-surface-sunken rounded-md mx-5 my-3 p-3"
        role="region"
        aria-label="Reassignment result"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold">Reassignment result</h3>
            <p className="mt-1 text-sm" style={MUTED}>
              {`${result.counts.reassigned} reassigned, ${result.counts.duplicate} already done, ${result.counts.skipped} skipped.`}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setResult(null)}
            className={SMALL_BTN}
            style={SECONDARY_BTN}
          >
            Dismiss
          </button>
        </div>
        <ul className="mt-2 text-sm">
          {shown.map((r) => (
            <li key={`${r.student_uuid}-${r.row_id}`}>
              {`${names.get(r.student_uuid) || r.student_uuid}: ${outcomeLabel(r.outcome)}`}
              {r.reason ? ` (${skipReasonLabel(r.reason)})` : ''}
            </li>
          ))}
        </ul>
        {result.results.length > RESULT_COLLAPSED ? (
          <button
            type="button"
            onClick={() => setResultExpanded((v) => !v)}
            className="mt-2 text-xs underline"
            style={{ background: 'transparent', border: 'none', margin: 0, padding: 0 }}
          >
            {resultExpanded ? 'Show fewer' : `Show all ${result.results.length}`}
          </button>
        ) : null}
      </div>
    );
  };

  const renderExpires = (iso: string) => {
    const d = daysLeft(iso, Date.now());
    return (
      <StackTd label="Expires" className={`${CELL} whitespace-nowrap`} title={formatTs(iso)}>
        <div>{formatDay(iso)}</div>
        {d !== null && d <= 0 ? <span className={WARN_PILL}>Expired</span> : null}
        {d !== null && d > 0 ? (
          <div className="text-xs text-content-muted">{`in ${d}d`}</div>
        ) : null}
      </StackTd>
    );
  };

  const renderRow = (r: AdvisorRow) => (
    <tr key={r.row_id} className={`border-t border-line hover:bg-surface-sunken ${ST.row}`}>
      {canWrite ? (
        <StackTd className={CELL}>
          <input
            type="checkbox"
            aria-label={`Select ${r.student_name}`}
            checked={selected.has(r.student_uuid)}
            onChange={() => toggle(r.student_uuid)}
          />
        </StackTd>
      ) : null}
      <StackTd className={`${CELL} truncate font-medium`} title={r.student_name}>
        <a
          href={studentProfilePath(r.student_uuid)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline"
        >
          {r.student_name || r.student_uuid}
        </a>
      </StackTd>
      <StackTd label="Source" className={`${CELL} whitespace-nowrap`}>
        <span
          className={`${PILL} border border-line max-w-full truncate align-middle`}
          title={sourceLabel(r.source)}
        >
          {sourceLabel(r.source)}
        </span>
      </StackTd>
      <StackTd
        label="Assigned"
        className={`${CELL} whitespace-nowrap`}
        title={formatTs(r.assigned_at)}
      >
        {formatDay(r.assigned_at)}
      </StackTd>
      {renderExpires(r.expires_at)}
      <StackTd
        label="Assigned by"
        className={`${CELL} truncate`}
        title={r.assigned_by_name || undefined}
      >
        {dash(r.assigned_by_name)}
      </StackTd>
      <StackTd className="px-3 py-2.5">
        {/* SECONDARY_BTN zeroes margins inline, so space the pair with a flex gap. */}
        <div className="flex justify-end max-md:justify-start gap-2 whitespace-nowrap">
          <button
            type="button"
            onClick={() => setHistoryStudent({ uuid: r.student_uuid, name: r.student_name })}
            aria-label={`History of ${r.student_name}`}
            className={SMALL_BTN}
            style={SECONDARY_BTN}
          >
            History
          </button>
          {canWrite ? (
            <button
              type="button"
              onClick={() => setEndTarget(r)}
              aria-label={`End assignment of ${r.student_name}`}
              className={SMALL_BTN}
              style={SECONDARY_BTN}
            >
              End
            </button>
          ) : null}
        </div>
      </StackTd>
    </tr>
  );

  const renderEmptyRow = () => {
    let body: React.ReactNode;
    if (loading) body = 'Loading students…';
    else if (hasNarrowingFilters(applied)) {
      body = (
        <>
          No students match these filters.{' '}
          <button
            type="button"
            onClick={resetFilters}
            className="underline"
            style={{ background: 'transparent', border: 'none', margin: 0, padding: 0 }}
          >
            Clear filters
          </button>
        </>
      );
    } else body = `${advisorName || 'This advisor'} has no active students.`;
    return (
      <tr className={ST.row}>
        <StackTd colSpan={cols.length} className="px-4 py-8 text-center" style={MUTED}>
          {body}
        </StackTd>
      </tr>
    );
  };

  const renderRows = () => {
    if (loadError && rows.length === 0) {
      return (
        <div className="px-4 py-8 text-center">
          <div role="alert" style={{ color: 'var(--state-error)' }}>
            {loadError.forbidden
              ? loadError.reason
              : `Could not load advisors: ${loadError.reason}`}
          </div>
          {loadError.forbidden ? null : (
            <button
              type="button"
              onClick={reload}
              disabled={loading}
              className="mt-3 rounded-md px-3 py-1.5 text-xs font-medium"
              style={PRIMARY_BTN}
            >
              {loading ? 'Retrying…' : 'Retry'}
            </button>
          )}
        </div>
      );
    }
    return (
      <>
        <table className={ST.table} aria-label="Active advisor assignments">
          <colgroup>
            {cols.map((c, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <col key={i} style={c.width ? { width: c.width } : undefined} />
            ))}
          </colgroup>
          {/* Card mode keeps only the select-all checkbox of the header (none when read-only). */}
          <thead
            className={`bg-surface-sunken text-content-secondary ${canWrite ? 'max-md:block' : 'max-md:hidden'}`}
          >
            <tr className="max-md:block">
              {cols.map((c, i) =>
                canWrite && i === 0 ? (
                  <th key="select" className={`${CELL} font-medium max-md:block`}>
                    <input
                      type="checkbox"
                      aria-label="Select all loaded"
                      checked={allSelected}
                      onChange={toggleAll}
                      disabled={rows.length === 0}
                    />
                  </th>
                ) : (
                  // eslint-disable-next-line react/no-array-index-key
                  <th key={`${c.label}-${i}`} className={`${CELL} font-medium max-md:hidden`}>
                    {c.label}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody className={ST.body}>
            {rows.length === 0 ? renderEmptyRow() : rows.map(renderRow)}
          </tbody>
        </table>
        {nextCursor > 0 ? (
          <div className="px-5 py-3 border-t border-line">
            <button
              type="button"
              onClick={loadMore}
              disabled={loadingMore}
              className={BTN}
              style={SECONDARY_BTN}
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          </div>
        ) : null}
      </>
    );
  };

  const renderEndModal = () => (
    <CustomModal
      show={!!endTarget}
      close={() => (ending ? undefined : setEndTarget(null))}
      title="End assignment"
      inner_style={{ width: 'min(480px, 96vw)', borderRadius: 12 }}
    >
      {endTarget ? (
        <div role="alertdialog" aria-label="Confirm end assignment">
          <p className="text-sm">
            {`End ${endTarget.student_name}'s assignment with ${advisorName || endTarget.advisor_name}? Attribution stops now.`}
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setEndTarget(null)}
              disabled={ending}
              className={BTN}
              style={SECONDARY_BTN}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onEndConfirm}
              disabled={ending}
              className={BTN}
              style={PRIMARY_BTN}
            >
              {ending ? 'Ending…' : 'Yes, end assignment'}
            </button>
          </div>
        </div>
      ) : null}
    </CustomModal>
  );

  return (
    <section className="bg-surface-card rounded-lg border border-line shadow-sm mx-4 my-5">
      {renderHeader()}
      {unknownAdvisor ? (
        <div
          role="alert"
          className="mx-5 my-4 rounded-md p-3 text-sm bg-state-warning-bg text-state-warning-text"
        >
          This advisor has no active students and is not on the sales roster. Pick another one from
          Switch advisor, or go back to All advisors.
        </div>
      ) : (
        <>
          {renderFilters()}
          {renderActions()}
          {renderResult()}
          {renderRows()}
        </>
      )}
      {canWrite ? renderPreviewModal() : null}
      <AdvisorHistoryModal student={historyStudent} onClose={() => setHistoryStudent(null)} />
      {canWrite ? renderEndModal() : null}
    </section>
  );
}

// Route: /sales-task-list/advisors/:advisorUuid (opened in a new tab from the Advisors tab).
function AdvisorStudentsPage() {
  const { advisorUuid } = useParams<{ advisorUuid: string }>();
  const accessType = useSelector(
    (state: { home: { access_type?: string } }) => state.home.access_type
  );
  const { access, canWrite } = useAdvisorAccess(hasAccess(accessType || '', 'SUPER_USER'));
  const dispatch = useDispatch();
  const notify = useCallback<Notify>(
    (type, message) => dispatch(actions.set_notification_variable(true, type, message)),
    [dispatch]
  );

  let body: React.ReactNode;
  if (access === 'allowed') {
    // key: switching advisors remounts, so filters, selection and results never carry over.
    body = (
      <AdvisorStudents
        key={advisorUuid}
        advisorUuid={advisorUuid}
        notify={notify}
        canWrite={canWrite}
      />
    );
  } else {
    body = (
      <section className="bg-surface-card rounded-lg border border-line shadow-sm mx-4 my-5 px-5 py-6 text-sm">
        <span role={access === 'denied' ? 'alert' : undefined} style={MUTED}>
          {access === 'denied' ? 'Super user or sales manager only' : 'Loading…'}
        </span>
      </section>
    );
  }
  return (
    <div>
      <CommonHeader title="Advisor students" />
      {body}
    </div>
  );
}

export default AdvisorStudentsPage;
